import {
  createApi,
  fetchBaseQuery,
  type BaseQueryApi,
} from "@reduxjs/toolkit/query/react";
import type {RootState} from "../index";
import {addToast} from "../slices/uiSlice";

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URL || "http://localhost:3001/api",
  prepareHeaders: (headers, {getState, endpoint}) => {
    const state = getState() as RootState;
    const token = state.auth.token;

    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }

    // ✅ FIXED: Don't set Content-Type for FormData uploads
    if (!headers.has("content-type")) {
      const isUploadEndpoint =
        endpoint === "uploadPropertyPhotos" ||
        endpoint === "uploadUserAvatar" ||
        endpoint === "createBanner" ||
        endpoint === "updateBannerWithImage" ||
        endpoint === "createPropertyWithPhotos"; // ✅ ADD THIS LINE

      if (!isUploadEndpoint) {
        headers.set("Content-Type", "application/json");
      }
      // ✅ For upload endpoints, don't set Content-Type
      // Browser will automatically set multipart/form-data with boundary
    }

    headers.set("Accept", "application/json");
    return headers;
  },
  // ✅ Enhanced fetchFn for better FormData debugging
  fetchFn: async (input, init) => {
    // Debug logging for FormData requests
    if (init?.body instanceof FormData) {
      console.log("🔄 Sending FormData request to:", input);
      console.log("📦 FormData entries:");
      for (const [key, value] of (init.body as FormData).entries()) {
        if (value instanceof File) {
          console.log(
            `  ${key}: File(${value.name}, ${value.type}, ${value.size} bytes)`
          );
        } else {
          console.log(`  ${key}: ${value}`);
        }
      }
      console.log("📋 Headers:", Object.fromEntries(init.headers as Headers));

      // ✅ IMPORTANT: Ensure Content-Type is not set for FormData
      if (init.headers) {
        if (init.headers instanceof Headers) {
          init.headers.delete("content-type");
          init.headers.delete("Content-Type");
        } else if (typeof init.headers === "object") {
          delete (init.headers as any)["content-type"];
          delete (init.headers as any)["Content-Type"];
        }
      }

      console.log("✅ Content-Type removed for FormData request");
    }

    return fetch(input, init);
  },
});

// Track if logout is already in progress to prevent multiple calls
let logoutInProgress = false;

const baseQueryWithReauth = async (
  args: Parameters<typeof baseQuery>[0],
  api: BaseQueryApi,
  extraOptions: Parameters<typeof baseQuery>[2]
) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    const state = api.getState() as RootState;

    if (state.auth.token && !logoutInProgress) {
      logoutInProgress = true;

      // Get error message from response
      const errorData = result.error.data as any;
      const errorMessage = errorData?.message || "Your session has expired";

      // Determine if this is an auth endpoint or protected endpoint
      const endpoint = typeof args === "string" ? args : args.url;
      const isAuthEndpoint = endpoint?.includes("/auth/");

      if (isAuthEndpoint) {
        // For auth endpoints (login/register), show error toast without logout
        api.dispatch(
          addToast({
            type: "error",
            title: "Authentication Failed",
            message: errorMessage,
            duration: 5000,
          })
        );
      } else {
        // For protected endpoints, show session expired warning
        api.dispatch(
          addToast({
            type: "warning",
            title: "Session Expired",
            message:
              "Your session has expired. Please login again to continue.",
            duration: 8000, // Longer duration for important message
          })
        );

        // Store current location for redirect after login
        const currentPath = window.location.pathname + window.location.search;
        localStorage.setItem("redirectAfterLogin", currentPath);

        // Delay logout slightly to allow user to read the message
        setTimeout(() => {
          api.dispatch({type: "auth/logout"});
          window.location.href = "/login";
        }, 2000); // 2 second delay
      }

      // Reset flag after delay
      setTimeout(() => {
        logoutInProgress = false;
      }, 3000);
    }
  }

  // Handle other common errors with toast notifications
  if (result.error) {
    const status = result.error.status;
    const errorData = result.error.data as any;

    switch (status) {
      case 403:
        api.dispatch(
          addToast({
            type: "error",
            title: "Access Denied",
            message:
              errorData?.message ||
              "You don't have permission to perform this action",
            duration: 5000,
          })
        );
        break;

      case 429:
        api.dispatch(
          addToast({
            type: "warning",
            title: "Too Many Requests",
            message: "Please wait a moment before trying again",
            duration: 5000,
          })
        );
        break;

      case 500:
      case 502:
      case 503:
        api.dispatch(
          addToast({
            type: "error",
            title: "Server Error",
            message: "Something went wrong on our end. Please try again later.",
            duration: 6000,
          })
        );
        break;

      case "FETCH_ERROR":
      case "TIMEOUT_ERROR":
        api.dispatch(
          addToast({
            type: "error",
            title: "Network Error",
            message: "Please check your internet connection and try again",
            duration: 5000,
          })
        );
        break;
    }
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "Auth",
    "User",
    "Property",
    "PropertyType",
    "PropertyPhoto",
    "Region",
    "Township",
    "Inquiry",
    "Contact",
    "PriceRange",
    "Dashboard",
    "Favorite",
    "PriceRangeReport",
    "Banner",
    "PropertyContact",
  ],
  endpoints: () => ({}),
});
